import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import courseConfig from "../config/course";
import { initPayment, verifyPayment } from "../services/api";

const CURRENCIES = [
  { code: "NGN", label: "Nigerian Naira (₦)", flag: "🇳🇬" },
  { code: "GHS", label: "Ghanaian Cedi (GH₵)", flag: "🇬🇭" },
  { code: "KES", label: "Kenyan Shilling (KSh)", flag: "🇰🇪" },
  { code: "RWF", label: "Rwandan Franc (RF)", flag: "🇷🇼" },
  { code: "UGX", label: "Ugandan Shilling (USh)", flag: "🇺🇬" },
  { code: "TZS", label: "Tanzanian Shilling (TSh)", flag: "🇹🇿" },
  { code: "ZAR", label: "South African Rand (R)", flag: "🇿🇦" },
  { code: "XAF", label: "Central African CFA (FCFA)", flag: "🇨🇲" },
  { code: "XOF", label: "West African CFA (CFA)", flag: "🇸🇳" },
  { code: "ZMW", label: "Zambian Kwacha (ZK)", flag: "🇿🇲" },
  { code: "EGP", label: "Egyptian Pound (E£)", flag: "🇪🇬" },
  { code: "GBP", label: "British Pound (£)", flag: "🇬🇧" },
  { code: "EUR", label: "Euro (€)", flag: "🇪🇺" },
  { code: "USD", label: "US Dollar ($)", flag: "🇺🇸" },
];

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    background: "#ffffff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "440px",
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
  },
  header: {
    padding: "24px 24px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: 700,
    color: "#0f172a",
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "8px",
    display: "flex",
  },
  body: {
    padding: "24px",
  },
  label: {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "1rem",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    fontFamily: "inherit",
    marginBottom: "16px",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  select: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "1rem",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    fontFamily: "inherit",
    marginBottom: "24px",
    background: "#ffffff",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  button: {
    width: "100%",
    padding: "16px",
    fontSize: "1rem",
    fontWeight: 800,
    color: "#000000",
    background: courseConfig.accentColor,
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontFamily: "inherit",
    boxShadow: `0 4px 16px ${courseConfig.accentColor}40`,
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  error: {
    color: "#ef4444",
    fontSize: "0.85rem",
    marginBottom: "16px",
    padding: "10px 14px",
    background: "#fef2f2",
    borderRadius: "8px",
    border: "1px solid #fecaca",
  },
  successBox: {
    textAlign: "center",
    padding: "32px 24px",
  },
  successIcon: {
    marginBottom: "16px",
  },
  successTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "12px",
  },
  successText: {
    fontSize: "0.95rem",
    color: "#64748b",
    lineHeight: 1.6,
    marginBottom: "24px",
  },
  courseLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "14px 32px",
    fontSize: "1rem",
    fontWeight: 800,
    color: "#000000",
    background: courseConfig.accentColor,
    borderRadius: "12px",
    textDecoration: "none",
    transition: "transform 0.2s",
  },
  emailNote: {
    fontSize: "0.85rem",
    color: "#94a3b8",
    marginTop: "16px",
  },
};

function CheckoutModal({ onClose }) {
  const [step, setStep] = useState("form"); // form | loading | success
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState("NGN");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [courseLink, setCourseLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Init payment on backend
      const paymentData = await initPayment({ email, name, currency });

      // Step 2: Open Flutterwave checkout
      let paymentCompleted = false;
      window.FlutterwaveCheckout({
        public_key: paymentData.publicKey,
        tx_ref: paymentData.txRef,
        amount: paymentData.amount,
        currency: currency,
        payment_options: "card, mobilemoney, ussd, banktransfer",
        customer: {
          email: email,
          name: name,
        },
        customizations: {
          title: "MooveTV",
          description: paymentData.courseName,
          logo: paymentData.logo || "",
        },
        callback: async (response) => {
          // Payment completed — immediately start verifying behind the Flutterwave modal
          paymentCompleted = true;
          setStep("loading");
          try {
            let attempts = 0;
            let result = null;

            while (attempts < 10) {
              await new Promise((resolve) => setTimeout(resolve, 2000));
              result = await verifyPayment(paymentData.txRef);
              if (result.status === "paid") break;
              attempts++;
            }

            if (result && result.status === "paid") {
              setCourseLink(result.driveLink);
              setStep("success");
              if (window.fbq) {
                window.fbq("track", "Purchase", {
                  value: paymentData.amount,
                  currency: currency,
                  content_name: courseConfig.courseName,
                });
              }
            } else {
              setCourseLink("");
              setStep("success");
            }
          } catch (err) {
            setCourseLink("");
            setStep("success");
          }
        },
        onclose: () => {
          // Only reset to form if payment was NOT completed
          if (!paymentCompleted) {
            setLoading(false);
          }
        },
      });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <motion.div
      style={styles.overlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && step === "form") onClose();
      }}
    >
      <motion.div
        style={styles.modal}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {/* Form Step */}
        {step === "form" && (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>Get {courseConfig.courseName}</h2>
              <button style={styles.closeBtn} onClick={onClose}>
                <X size={20} color="#94a3b8" />
              </button>
            </div>
            <form style={styles.body} onSubmit={handleSubmit}>
              {error && <p style={styles.error}>{error}</p>}

              <label style={styles.label}>Full Name</label>
              <input
                style={styles.input}
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={(e) =>
                  (e.target.style.borderColor = courseConfig.accentColor)
                }
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />

              <label style={styles.label}>Email Address</label>
              <input
                style={styles.input}
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={(e) =>
                  (e.target.style.borderColor = courseConfig.accentColor)
                }
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />

              <label style={styles.label}>Payment Currency</label>
              <select
                style={styles.select}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.label}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2
                      size={18}
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    Processing...
                  </>
                ) : (
                  "Proceed to Payment"
                )}
              </button>
            </form>
          </>
        )}

        {/* Loading Step */}
        {step === "loading" && (
          <div style={{ ...styles.successBox, padding: "60px 24px" }}>
            <Loader2
              size={48}
              color={courseConfig.accentColor}
              style={{
                animation: "spin 1s linear infinite",
                marginBottom: "20px",
              }}
            />
            <p
              style={{ fontSize: "1.1rem", fontWeight: 600, color: "#0f172a" }}
            >
              Confirming your payment...
            </p>
            <p
              style={{ fontSize: "0.9rem", color: "#94a3b8", marginTop: "8px" }}
            >
              This may take a few seconds.
            </p>
          </div>
        )}

        {/* Success Step */}
        {step === "success" && (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>
              <CheckCircle2 size={56} color="#10b981" />
            </div>
            <h3 style={styles.successTitle}>Payment Successful!</h3>
            <p style={styles.successText}>
              Thank you for purchasing {courseConfig.courseName}. Your download
              is ready.
            </p>
            {courseLink ? (
              <a
                href={courseLink}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.courseLink}
              >
                Download Your Templates <ExternalLink size={16} />
              </a>
            ) : (
              <p style={{ ...styles.successText, marginBottom: 0 }}>
                We're preparing your download link. Check your email shortly.
              </p>
            )}
            <p style={styles.emailNote}>
              We've also sent the download link to your email.
            </p>
          </div>
        )}
      </motion.div>

      <style>{`
       @keyframes spin {
         from { transform: rotate(0deg); }
         to { transform: rotate(360deg); }
       }
     `}</style>
    </motion.div>
  );
}

export default CheckoutModal;

// import React, { useState } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { X, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
// import courseConfig from '../config/course';
// import { initPayment, verifyPayment } from '../services/api';

// const CURRENCIES = [
//   { code: 'NGN', label: 'Nigerian Naira (₦)', flag: '🇳🇬' },
//   { code: 'GHS', label: 'Ghanaian Cedi (GH₵)', flag: '🇬🇭' },
//   { code: 'KES', label: 'Kenyan Shilling (KSh)', flag: '🇰🇪' },
//   { code: 'RWF', label: 'Rwandan Franc (RF)', flag: '🇷🇼' },
//   { code: 'UGX', label: 'Ugandan Shilling (USh)', flag: '🇺🇬' },
//   { code: 'TZS', label: 'Tanzanian Shilling (TSh)', flag: '🇹🇿' },
//   { code: 'ZAR', label: 'South African Rand (R)', flag: '🇿🇦' },
//   { code: 'XAF', label: 'Central African CFA (FCFA)', flag: '🇨🇲' },
//   { code: 'XOF', label: 'West African CFA (CFA)', flag: '🇸🇳' },
//   { code: 'ZMW', label: 'Zambian Kwacha (ZK)', flag: '🇿🇲' },
//   { code: 'EGP', label: 'Egyptian Pound (E£)', flag: '🇪🇬' },
//   { code: 'GBP', label: 'British Pound (£)', flag: '🇬🇧' },
//   { code: 'EUR', label: 'Euro (€)', flag: '🇪🇺' },
//   { code: 'USD', label: 'US Dollar ($)', flag: '🇺🇸' },
// ];

// const styles = {
//   overlay: {
//     position: 'fixed',
//     inset: 0,
//     background: 'rgba(0,0,0,0.6)',
//     backdropFilter: 'blur(4px)',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     zIndex: 1000,
//     padding: '20px',
//   },
//   modal: {
//     background: '#ffffff',
//     borderRadius: '20px',
//     width: '100%',
//     maxWidth: '440px',
//     maxHeight: '90vh',
//     overflow: 'auto',
//     position: 'relative',
//   },
//   header: {
//     padding: '24px 24px 0',
//     display: 'flex',
//     justifyContent: 'space-between',
//     alignItems: 'center',
//   },
//   title: {
//     fontSize: '1.3rem',
//     fontWeight: 700,
//     color: '#0f172a',
//   },
//   closeBtn: {
//     background: 'none',
//     border: 'none',
//     cursor: 'pointer',
//     padding: '4px',
//     borderRadius: '8px',
//     display: 'flex',
//   },
//   body: {
//     padding: '24px',
//   },
//   label: {
//     display: 'block',
//     fontSize: '0.85rem',
//     fontWeight: 600,
//     color: '#374151',
//     marginBottom: '6px',
//   },
//   input: {
//     width: '100%',
//     padding: '12px 16px',
//     fontSize: '1rem',
//     border: '1.5px solid #e2e8f0',
//     borderRadius: '10px',
//     outline: 'none',
//     fontFamily: 'inherit',
//     marginBottom: '16px',
//     boxSizing: 'border-box',
//     transition: 'border-color 0.2s',
//   },
//   select: {
//     width: '100%',
//     padding: '12px 16px',
//     fontSize: '1rem',
//     border: '1.5px solid #e2e8f0',
//     borderRadius: '10px',
//     outline: 'none',
//     fontFamily: 'inherit',
//     marginBottom: '24px',
//     background: '#ffffff',
//     boxSizing: 'border-box',
//     cursor: 'pointer',
//   },
//   button: {
//     width: '100%',
//     padding: '16px',
//     fontSize: '1rem',
//     fontWeight: 800,
//     color: '#000000',
//     background: courseConfig.accentColor,
//     border: 'none',
//     borderRadius: '12px',
//     cursor: 'pointer',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     gap: '8px',
//     fontFamily: 'inherit',
//     boxShadow: `0 4px 16px ${courseConfig.accentColor}40`,
//   },
//   buttonDisabled: {
//     opacity: 0.6,
//     cursor: 'not-allowed',
//   },
//   error: {
//     color: '#ef4444',
//     fontSize: '0.85rem',
//     marginBottom: '16px',
//     padding: '10px 14px',
//     background: '#fef2f2',
//     borderRadius: '8px',
//     border: '1px solid #fecaca',
//   },
//   successBox: {
//     textAlign: 'center',
//     padding: '32px 24px',
//   },
//   successIcon: {
//     marginBottom: '16px',
//   },
//   successTitle: {
//     fontSize: '1.4rem',
//     fontWeight: 700,
//     color: '#0f172a',
//     marginBottom: '12px',
//   },
//   successText: {
//     fontSize: '0.95rem',
//     color: '#64748b',
//     lineHeight: 1.6,
//     marginBottom: '24px',
//   },
//   courseLink: {
//     display: 'inline-flex',
//     alignItems: 'center',
//     gap: '8px',
//     padding: '14px 32px',
//     fontSize: '1rem',
//     fontWeight: 800,
//     color: '#000000',
//     background: courseConfig.accentColor,
//     borderRadius: '12px',
//     textDecoration: 'none',
//     transition: 'transform 0.2s',
//   },
//   emailNote: {
//     fontSize: '0.85rem',
//     color: '#94a3b8',
//     marginTop: '16px',
//   },
// };

// function CheckoutModal({ onClose }) {
//   const [step, setStep] = useState('form'); // form | loading | success
//   const [name, setName] = useState('');
//   const [email, setEmail] = useState('');
//   const [currency, setCurrency] = useState('NGN');
//   const [error, setError] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [courseLink, setCourseLink] = useState('');

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError('');

//     if (!name.trim()) {
//       setError('Please enter your full name.');
//       return;
//     }

//     if (!email.trim()) {
//       setError('Please enter your email address.');
//       return;
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(email.trim())) {
//       setError('Please enter a valid email address.');
//       return;
//     }

//     setLoading(true);

//     try {
//       // Step 1: Init payment on backend
//       const paymentData = await initPayment({ email, name, currency });

//       // Step 2: Open Flutterwave checkout
//       let paymentCompleted = false;
//       window.FlutterwaveCheckout({
//         public_key: paymentData.publicKey,
//         tx_ref: paymentData.txRef,
//         amount: paymentData.amount,
//         currency: currency,
//         payment_options: 'card, mobilemoney, ussd, banktransfer',
//         customer: {
//           email: email,
//           name: name,
//         },
//         customizations: {
//           title: 'MooveTV',
//           description: paymentData.courseName,
//           logo: paymentData.logo || '',
//         },
//         callback: async (response) => {
//           // Payment completed — immediately start verifying behind the Flutterwave modal
//           paymentCompleted = true;
//           setStep('loading');
//           try {
//             let attempts = 0;
//             let result = null;

//             while (attempts < 10) {
//               await new Promise(resolve => setTimeout(resolve, 2000));
//               result = await verifyPayment(paymentData.txRef);
//               if (result.status === 'paid') break;
//               attempts++;
//             }

//             if (result && result.status === 'paid') {
//               setCourseLink(result.driveLink);
//               setStep('success');
//             } else {
//               setCourseLink('');
//               setStep('success');
//             }
//           } catch (err) {
//             setCourseLink('');
//             setStep('success');
//           }
//         },
//         onclose: () => {
//           // Only reset to form if payment was NOT completed
//           if (!paymentCompleted) {
//             setLoading(false);
//           }
//         },
//       });
//     } catch (err) {
//       setError(err.message || 'Something went wrong. Please try again.');
//       setLoading(false);
//     }
//   };

//   return (
//     <motion.div
//       style={styles.overlay}
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//       onClick={(e) => {
//         if (e.target === e.currentTarget && step === 'form') onClose();
//       }}
//     >
//       <motion.div
//         style={styles.modal}
//         initial={{ scale: 0.95, opacity: 0 }}
//         animate={{ scale: 1, opacity: 1 }}
//         transition={{ duration: 0.2 }}
//       >
//         {/* Form Step */}
//         {step === 'form' && (
//           <>
//             <div style={styles.header}>
//               <h2 style={styles.title}>Get {courseConfig.courseName}</h2>
//               <button style={styles.closeBtn} onClick={onClose}>
//                 <X size={20} color="#94a3b8" />
//               </button>
//             </div>
//             <form style={styles.body} onSubmit={handleSubmit}>
//               {error && <p style={styles.error}>{error}</p>}

//               <label style={styles.label}>Full Name</label>
//               <input
//                 style={styles.input}
//                 type="text"
//                 placeholder="John Doe"
//                 value={name}
//                 onChange={(e) => setName(e.target.value)}
//                 onFocus={(e) => e.target.style.borderColor = courseConfig.accentColor}
//                 onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
//               />

//               <label style={styles.label}>Email Address</label>
//               <input
//                 style={styles.input}
//                 type="email"
//                 placeholder="john@example.com"
//                 value={email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 onFocus={(e) => e.target.style.borderColor = courseConfig.accentColor}
//                 onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
//               />

//               <label style={styles.label}>Payment Currency</label>
//               <select
//                 style={styles.select}
//                 value={currency}
//                 onChange={(e) => setCurrency(e.target.value)}
//               >
//                 {CURRENCIES.map((c) => (
//                   <option key={c.code} value={c.code}>
//                     {c.flag} {c.label}
//                   </option>
//                 ))}
//               </select>

//               <button
//                 type="submit"
//                 style={{
//                   ...styles.button,
//                   ...(loading ? styles.buttonDisabled : {}),
//                 }}
//                 disabled={loading}
//               >
//                 {loading ? (
//                   <>
//                     <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
//                     Processing...
//                   </>
//                 ) : (
//                   'Proceed to Payment'
//                 )}
//               </button>
//             </form>
//           </>
//         )}

//         {/* Loading Step */}
//         {step === 'loading' && (
//           <div style={{ ...styles.successBox, padding: '60px 24px' }}>
//             <Loader2
//               size={48}
//               color={courseConfig.accentColor}
//               style={{ animation: 'spin 1s linear infinite', marginBottom: '20px' }}
//             />
//             <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>
//               Confirming your payment...
//             </p>
//             <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginTop: '8px' }}>
//               This may take a few seconds.
//             </p>
//           </div>
//         )}

//         {/* Success Step */}
//         {step === 'success' && (
//           <div style={styles.successBox}>
//             <div style={styles.successIcon}>
//               <CheckCircle2 size={56} color="#10b981" />
//             </div>
//             <h3 style={styles.successTitle}>Payment Successful!</h3>
//             <p style={styles.successText}>
//               Thank you for purchasing {courseConfig.courseName}. Your download is ready.
//             </p>
//             {courseLink ? (
//               <a
//                 href={courseLink}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 style={styles.courseLink}
//               >
//                 Download Your Templates <ExternalLink size={16} />
//               </a>
//             ) : (
//               <p style={{ ...styles.successText, marginBottom: 0 }}>
//                 We're preparing your download link. Check your email shortly.
//               </p>
//             )}
//             <p style={styles.emailNote}>
//               We've also sent the download link to your email.
//             </p>
//           </div>
//         )}
//       </motion.div>

//       <style>{`
//         @keyframes spin {
//           from { transform: rotate(0deg); }
//           to { transform: rotate(360deg); }
//         }
//       `}</style>
//     </motion.div>
//   );
// }

// export default CheckoutModal;
